import axios, {AxiosResponse, Canceler} from 'axios';

type DownloadProcessController = {
  cancel: Canceler; // Function that cancels the download process.
  promise: Promise<void>;
};

export async function simpleFileDownload(
  fileName: string,
  mimeType: string,
  content: string
): Promise<void> {
  const {promise} = downloadFile(fileName, mimeType, content);

  await promise;
}

/**
 * Downloads a file with the specified name, MIME type, and content (supports concurrency donwloads).
 * @param fileName - The name of the file to be downloaded.
 * @param mimeType - The MIME type of the file.
 * @param content - The content of the file.
 */
export function downloadFile(
  fileName: string,
  mimeType: string,
  content: string
): DownloadProcessController {
  const blob = new Blob([content], {type: mimeType});
  const url = window.URL.createObjectURL(blob);

  return downloadBlob({fileName, blobUrl: url});
}

type DownloadBlobOptions = {
  fileName: string;
  blobUrl: string;
  bubbleUpError?: boolean;
};

export function downloadBlob({
  blobUrl,
  fileName,
  bubbleUpError = false,
}: DownloadBlobOptions): DownloadProcessController {
  const cancelToken = axios.CancelToken;
  const source = cancelToken.source();

  const promise = axios
    .get(blobUrl, {
      responseType: 'blob',
      cancelToken: source.token,
    })
    .then((response: AxiosResponse<Blob>) => {
      const urlCreator = window.URL || window.webkitURL;
      const fileUrl = urlCreator.createObjectURL(response.data);

      const tag = document.createElement('a');
      tag.href = fileUrl;
      tag.download = fileName;
      document.body.appendChild(tag);
      tag.click();

      document.body.removeChild(tag);
      URL.revokeObjectURL(blobUrl);
    })
    .catch(error => {
      if (!axios.isCancel(error)) {
        console.error('Error downloading file:', error);

        if (bubbleUpError) {
          throw error;
        }
      }
    });

  return {promise, cancel: source.cancel};
}

export type DownloadFileOptions = {
  url: string;
  showDownloadWindow?: boolean;
  fileName?: string;
};
export const downloadFileFromURL = ({
  url,
  showDownloadWindow,
  fileName,
}: DownloadFileOptions) => {
  const a = document.createElement('a');
  a.style.display = 'none';
  if (showDownloadWindow) {
    if (fileName) {
      a.setAttribute('download', fileName);
    } else {
      // defaults to the name of the file from the url.
      a.setAttribute('download', '');
    }
  }
  a.setAttribute('href', url);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
